import React, { useContext, useState, useRef } from 'react';
import { cloneDeep, omit } from 'lodash';

import { DataTransaction } from '../../common/grid/DataTransaction';
import { DataSource } from '../../common/grid/DataSource';

import { RestApiContext } from '../../contexts/RestApiContext';

import { useAggregatedState, useBusyState } from '../../../helpers';
import { DataChangeType, DataUpdateStatus, RowUpdateStatus } from '../../../helpers';

export const PageContext = React.createContext(null);

const { Provider } = PageContext;
export const PageProvider = ({ ...props }) => {
    const [busy, isBusy, setIsBusy] = useBusyState({});
    const [pageIsReady, setPageIsReady] = useState(false);
    const [state, setState] = useAggregatedState({
        table: null,
        notAvailable: false,
        dataTransaction: null,
        updateResult: null,
        gridState: null,
        lastGridState: null,
        selectedRowsCount: 0,
        filterModel: null,
        rowsInPage: 0
    });

    const apiClient = useContext(RestApiContext);

    const getTableAsync = async (tableId) => {
        try {
            const table = await apiClient.getAssignedTableAsync(tableId, false, true, false);
            setState({
                table,
                notAvailable: false,
                updateResult: null,
                gridState: JSON.parse(table.userState),
                lastGridState: JSON.parse(table.userState)
            });
        } catch (e) {
            console.debug(e);
            setState({
                table: null,
                notAvailable: true,
                updateResult: null,
                gridState: null,
                lastGridState: null
            });
        }
    }

    const loadTableRowsAsync = async (tableId, sortAndFilter) => {
        let sandf = JSON.parse(atob(sortAndFilter));
        setState({ filterModel: sandf.filterModel});
        try {
            const table = await apiClient.getAssignedTableAsync(tableId, false, true, true, sortAndFilter);
            setState({ rowsInPage: table.data.items.length })
            setState({ updateResult: null });
            return table.data;
        } catch (e) {
            console.debug(e);
        }
    }

    const updateTableStateAsync = async ({ tableId, newState }) => {
        try {
            await apiClient.updateTableStateAsync({ tableId, state: newState });
            setState({ lastGridState: newState });
        } catch (e) {
            console.debug(e);
        }
    }

    const resetTableStateAsync = async (tableId) => {
        await updateTableStateAsync({ tableId, state: null });
        setState({ gridState: null });
    }

    
    const updateTableContentAsync = async ({ tableId, transaction }) => {
        try {
            setState({ updateResult: null });
            const updateResult = await apiClient.updateTableContentAsync({ tableId, transaction });
            setState({ updateResult });
        } catch (e) {
            console.debug(e);
            setState({ updateResult: null });
        }
    }
    
    const importTableContentAsync = async ({ tableId, data, clearOnImport }) => {
        try {
            await apiClient.enqueueTableImportAsync({ tableId, data, clearOnImport });
            return true;
        } catch (e) {
            console.debug(e);
            return false;
        }
    }
    
    const exportTableContentAsync = async ({ tableId }) => {
        try {
            const json = JSON.stringify(dataSource.current.lastRequest);
            const sortAndFilter = btoa(json);
            await apiClient.enqueueTableExportAsync({ tableId, sortAndFilter });
            return true;
        } catch (e) {
            console.debug(e);
            return false;
        }
    }
    
    const [gridApi, setGridApi] = useState(null);
    const [gridColumnApi, setGridColumnApi] = useState(null);
    
    const dataSource = useRef(null);
    const createGridDataSource = ({ table, pageSize, filterModel, onLoadRows }) => {
        dataSource.current = new DataSource();
        dataSource.current.table = cloneDeep(table);
        dataSource.current.pageIndex = 0;
        dataSource.current.pageSize = pageSize;
        dataSource.current.filterModel = filterModel ?? null;
        dataSource.current.onLoadRows = onLoadRows;
    }

    const getGridDataSource = () => {
        return dataSource.current;
    }

    const getSelectedNodes = () => {
        try
        {
            return  gridApi.getSelectedNodes();
        } catch
        {
            return null;
        }
    }
    
    const getSelectedRows = () => {
        return gridApi.getSelectedRows();
    }
    
    const setDataChange = (rowData, changeType) => {
        const dataTransaction = state.dataTransaction ? state.dataTransaction.clone() : new DataTransaction();
        const changeId = rowData.changeId;
        const data = Object.keys(omit(rowData, ["uuid", "changeId", "isNew", "isChanged", "error", "errorInfo", "changeHash", "getChangeHash"]))
            .reduce((obj, key) => ({ ...obj, [key]: (rowData[key] ?? "").toString() }), { uuid: rowData.uuid });
        const dataChange = dataTransaction.setDataChange(changeId, data, changeType);
        setState({ dataTransaction });
        console.debug(dataTransaction);
        return dataChange;
    }
    
    const setSelectedRowsCount = (count) => {
        setState({ selectedRowsCount: count });
    }

    const setFilter = (filterModel) => {    
        setState({filterModel: filterModel});
    }

    const setDataChanges = (rowsData, changeType) => {
        const dataTransaction = state.dataTransaction ? state.dataTransaction.clone() : new DataTransaction();
        const dataChanges = rowsData.map((rowData) => {
            const changeId = rowData.changeId;
            const data = Object.keys(omit(rowData, ["uuid", "changeId", "isNew", "isChanged", "error", "errorInfo", "changeHash", "getChangeHash"]))
                .reduce((obj, key) => ({ ...obj, [key]: (rowData[key] ?? "").toString() }), { uuid: rowData.uuid });
            const dataChange = dataTransaction.setDataChange(changeId, data, changeType)
            return dataChange;
        });
        setState({ dataTransaction });
        console.debug(dataTransaction);
        return dataChanges;
    }

    const removeDataChanges = (rowsData) => {
        const dataTransaction = state.dataTransaction.clone();
        rowsData.forEach((rowData) => {
            dataTransaction.removeDataChange(rowData.changeId);
        });
        setState({ dataTransaction });
        console.debug(dataTransaction);
        return !dataTransaction.hasPendingChanges;
    }

    const cancelDataChanges = () => {
        const dataTransaction = new DataTransaction();
        setState({ dataTransaction, updateResult: null });
        console.debug(dataTransaction);
    }

    const addNewRow = () => {
        const columnNames = state.table.columns.map(x => x.name);
        const rowData = columnNames.reduce((obj, key) => ({ ...obj, [key]: undefined }), { uuid: undefined });
        const dataChange = setDataChange(rowData, DataChangeType.AddNew);
        dataSource.current.addRow({ api: gridApi, rowData: { changeId: dataChange.changeId, ...dataChange.data } });
    }

    const updateRow = (rowIndex) => {
        const rowNode = gridApi.getDisplayedRowAtIndex(rowIndex);
        const rowData = dataSource.current.ensureRowUUID(rowNode.data);
        const dataChange = setDataChange(rowData, DataChangeType.Update);
        dataSource.current.updateRow({ api: gridApi, rowIndex, rowData: { changeId: dataChange.changeId, ...dataChange.data } });
    }

    const updateRows = (rowIndexes) => {
        if (rowIndexes && rowIndexes.length > 0) {
            const rowsData = rowIndexes.map((rowIndex) => gridApi.getDisplayedRowAtIndex(rowIndex).data);
            const dataChanges = setDataChanges(rowsData, DataChangeType.Update);
            const rowUpdates = dataChanges.map((dataChange, index) => ({
                rowIndex: rowIndexes[index],
                rowData: { changeId: dataChange.changeId, ...dataChange.data }
            }));
            dataSource.current.updateRows({ api: gridApi, rowUpdates });
        }
    }

    const deleteRows = (rowIndexes) => {
        if (rowIndexes && rowIndexes.length > 0) {
            const rowsData = rowIndexes.map((rowIndex) => gridApi.getDisplayedRowAtIndex(rowIndex).data);
            setDataChanges(rowsData, DataChangeType.Delete);
            dataSource.current.deleteRows({ api: gridApi, rowIndexes });
        }
    }

    const deleteAllFilteredRows = async (tableId) => {
        await apiClient.deleteAllFiltered(tableId, state.gridState.filterModel);
        await clearGridStatusAsync();
        setIsBusy({ deleting: false });
    }

    const cancelRowChanges = (reload) => {
        cancelDataChanges();
        if (reload) {
            dataSource.current.cancelChanges({ api: gridApi });
        }
    }

    const stopEditingRows = (cancel) => {
        dataSource.current.stopEditing({ api: gridApi, cancel });
    }

    const deselectAllRows = () => {
        dataSource.current.deselectAll({ api: gridApi });
    }

    const refreshRowsStatus = (updateResult) => {
        // Consider all successfully row update results as completed transactions
        // That is, all of them must be removed from the current data transaction
        const rowsData = updateResult.rows.filter(x => x.status === RowUpdateStatus.Ok).map(x => ({ changeId: x.changeId }));
        // Check all row nodes that have a corresponding row update result
        // Use the row update result to refresh row nodes with new information
        const rowsStatus = [];
        gridApi.forEachNode((rowNode, rowIndex) => {
            const changeId = rowNode.data.changeId;
            if (changeId) {
                const rowResult = updateResult.rows.find(x => x.changeId === changeId);
                if (rowResult) {
                    rowsStatus.push({ rowIndex, status: rowResult.status, message: rowResult.message, updatedData: rowResult.data, errorInfo: rowResult.errorInfo });
                }
            }
        });
        // Refresh everything
        if (rowsData.length > 0) {
            const transactionDone = removeDataChanges(rowsData);
            if (transactionDone) {
                dataSource.current.reloadRows({ api: gridApi });
            }

        }
        if (rowsStatus.length > 0) {
            dataSource.current.refreshRowsStatus({ api: gridApi, rowsStatus });
        }
    }

    const loadMoreRows = (pageIndex, pageSize) => {
        dataSource.current.reloadRows({ api: gridApi, pageIndex, pageSize });
    }

    const saveColumnVisibleStatus = (columnName, isVisible) => {
        const gridState = cloneDeep(state.gridState ?? {});
        gridState.visibility = { ...gridState.visibility, [columnName]: isVisible };
        setState({ gridState });
        console.debug(gridState);

    }

    const saveColumnPinnedStatus = (columnName, pinned) => {
        const gridState = cloneDeep(state.gridState ?? {});
        gridState.pins = { ...gridState.pins, [columnName]: pinned };
        setState({ gridState });
        console.debug(gridState);
    }

    const saveFilterModelStatus = (filterModel) => {
        const gridState = cloneDeep(state.gridState ?? {});
        gridState.filterModel = cloneDeep(filterModel);
        setState({ gridState });
        console.debug(gridState);
    }

    const clearGridStatus = () => {
        gridApi.setFilterModel(null);
        const columnNames = state.table.columns.map(x => x.name);
        gridColumnApi.setColumnsVisible(columnNames, true);
        gridColumnApi.setColumnsPinned(columnNames, null);
    }

    const clearGridStatusAsync = async () => {
        gridApi.setFilterModel(null);
        const columnNames = state.table.columns.map(x => x.name);
        gridColumnApi.setColumnsVisible(columnNames, true);
        gridColumnApi.setColumnsPinned(columnNames, null);
    }

    return (
        <Provider value={{
            state,
            pageIsReady,
            setPageIsReady,
            isBusy,
            setIsBusy,
            getTableAsync,
            loadTableRowsAsync,
            updateTableStateAsync,
            gridApi,
            setFilter,
            resetTableStateAsync,
            updateTableContentAsync,
            importTableContentAsync,
            exportTableContentAsync,
            setGridApi,
            setGridColumnApi,
            createGridDataSource,
            getGridDataSource,
            getSelectedNodes,
            getSelectedRows,
            addNewRow,
            updateRow,
            updateRows,
            deleteRows,
            cancelRowChanges,
            stopEditingRows,
            deselectAllRows,
            refreshRowsStatus,
            loadMoreRows,
            saveColumnVisibleStatus,
            saveColumnPinnedStatus,
            saveFilterModelStatus,
            clearGridStatus,
            setSelectedRowsCount,
            deleteAllFilteredRows
        }}>
            {props.children}
        </Provider>
    );
}